import React, { useRef, useState } from 'react';
import { cancelInvoice } from '../../services/accountingInvoiceSelection';

/**
 * Cancel Invoice — the port of V1's `chkdelete` prompt for `cancelinvoice`
 * (main.pl:8355). V1 refuses to submit with an empty box ("Reason for Delete must be
 * filled out") because the reason is the only record of why the charges came back: it is
 * written as a note on every pro on the invoice.
 *
 * The copy says what cancelling actually does, which is not what "cancel" suggests —
 * nothing is destroyed. The invoice header is soft-deleted, every charge goes back to
 * uninvoiced and can be billed again, and the PDF already sent to the customer survives.
 */
const CancelInvoiceDialog: React.FC<{
  invhistoryrecno: number;
  invoicenumber: string;
  proCount: number;
  onClose: () => void;
  onCancelled: () => void;
}> = ({ invhistoryrecno, invoicenumber, proCount, onClose, onCancelled }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A ref, not the `busy` state: two clicks in the same tick both read the pre-render
  // value of state and both submit. Cancelling twice is not idempotent — the second call
  // is refused, but only after the first has already released the charges. Same guard as
  // the leg-save double-submit fix.
  const submitting = useRef(false);
  const trimmed = reason.trim();

  const submit = async () => {
    if (!trimmed || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      await cancelInvoice(invhistoryrecno, trimmed);
      onCancelled();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not cancel the invoice.');
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50 p-4" role="dialog" aria-modal="true" aria-label="Cancel invoice">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-rule bg-panel shadow-xl">
        <div className="border-b border-hairline px-4 py-3">
          <h2 className="text-base font-bold text-heading">Cancel invoice {invoicenumber}</h2>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-body">
          <p>
            The charges go back to uninvoiced and can be billed again. The invoice record is
            kept and marked cancelled, and its PDF stays available.
          </p>
          <label className="block">
            <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted">
              Reason for cancelling (required)
            </span>
            <textarea
              autoFocus
              rows={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={2000}
              className="w-full rounded border border-rule bg-input px-2 py-1.5 text-sm text-body"
              placeholder="Why is this invoice being cancelled?"
            />
          </label>
          <p className="text-xs text-muted">
            Saved as a note on {proCount === 1 ? 'the pro' : `all ${proCount} pros`} on this invoice.
          </p>
          {error && <p role="alert" className="text-sm text-danger-fg">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-hairline bg-sunken px-4 py-3">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-label underline disabled:opacity-50">
            Keep invoice
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!trimmed || busy}
            className="rounded-md bg-brand-red px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Cancelling…' : 'Cancel invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelInvoiceDialog;
